import { useEffect, useState, type ChangeEvent } from 'react'
import { Box, Slider, Stack, Typography } from '@mui/material'

interface DeviationBandSliderProps {
  /** Max low deviation, percent below expected (0–100). */
  low: number
  /** Max high deviation, percent above expected (0–100). */
  high: number
  disabled?: boolean
  onChange: (low: number, high: number) => void
}

const OK_GREEN = '#2f7d54'
const BAND_FILL = 'rgba(47, 125, 84, 0.9)'
const OUTSIDE = 'rgba(220, 53, 69, 0.22)'

const clampPct = (value: number) => Math.min(100, Math.max(0, Math.round(value)))

interface PctFieldProps {
  /** Drawn before the number: which side of expected this limit sits on. */
  sign: string
  value: number
  disabled?: boolean
  ariaLabel: string
  onChange: (value: number) => void
}

/**
 * A limit typed rather than dragged. The slider and this field are two views
 * of the same number: dragging refreshes the text, typing moves the thumb.
 * While the field has focus it owns its own text, so a half-typed "1" on the
 * way to "15" is not overwritten by the clamped value bouncing back.
 */
const PctField = ({
  sign,
  value,
  disabled,
  ariaLabel,
  onChange,
}: PctFieldProps) => {
  const [text, setText] = useState(String(value))
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    if (!editing) setText(String(value))
  }, [value, editing])

  const handleChange = (raw: string) => {
    // Digits only: the scale is whole percent, and the sign is fixed by which
    // side of the band this field belongs to.
    const cleaned = raw.replace(/[^0-9]/g, '').slice(0, 3)
    setText(cleaned)
    if (cleaned === '') return
    const next = clampPct(Number(cleaned))
    if (next !== value) onChange(next)
  }

  const handleBlur = () => {
    setEditing(false)
    // An empty or over-range field falls back to what the slider actually holds.
    const next = text === '' ? value : clampPct(Number(text))
    setText(String(next))
    if (next !== value) onChange(next)
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '1px',
        height: '28px',
        px: '8px',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'border.main',
        backgroundColor: 'background.default',
        opacity: disabled ? 0.6 : 1,
        '&:hover': { borderColor: disabled ? 'border.main' : 'rgba(0, 71, 68, 0.5)' },
        '&:focus-within': { borderColor: 'primary.main' },
      }}
    >
      <Box
        component="span"
        sx={{
          fontSize: '13px',
          fontWeight: 700,
          fontFamily: 'monospace',
          color: 'text.main',
        }}
      >
        {sign}
      </Box>
      <Box
        component="input"
        value={text}
        disabled={disabled}
        aria-label={ariaLabel}
        inputMode="numeric"
        onFocus={() => setEditing(true)}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          handleChange(event.target.value)
        }
        onBlur={handleBlur}
        sx={{
          width: '3ch',
          p: 0,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          textAlign: 'right',
          fontFamily: 'monospace',
          fontSize: '13.5px',
          fontWeight: 700,
          color: 'text.main',
        }}
      />
      <Box
        component="span"
        sx={{ fontSize: '11.5px', fontWeight: 700, color: 'grey.800' }}
      >
        %
      </Box>
    </Box>
  )
}

/**
 * The alarm envelope as a band around the expected flow.
 *
 * The scale runs −100%…+100% of expected; the left thumb is the low limit,
 * the right thumb the high limit, and the green stretch between them is what
 * the controller accepts. Red creeps in from the rail's ends so tightening a
 * thumb visibly grows the zone that alarms. A thumb parked at ±100 means that
 * side never alarms — which is exactly what the seeded defaults do.
 */
const DeviationBandSlider = ({
  low,
  high,
  disabled,
  onChange,
}: DeviationBandSliderProps) => {
  const value = [-clampPct(low), clampPct(high)]

  const handleChange = (_event: Event, next: number | number[]) => {
    if (!Array.isArray(next)) return
    // Each thumb stays on its own side of the expected line: the left one is
    // "how far below", the right one "how far above" — a crossed pair has no
    // meaning to the controller.
    const lowSide = Math.min(0, next[0])
    const highSide = Math.max(0, next[1])
    onChange(clampPct(-lowSide), clampPct(highSide))
  }

  return (
    <Box sx={{ px: '10px', minWidth: 0, overflow: 'visible' }}>
      <Slider
        value={value}
        onChange={handleChange}
        disabled={disabled}
        min={-100}
        max={100}
        step={1}
        disableSwap
        // No floating pills at all: they collided when the band was tight, and
        // lingered as an empty box after a drag while the thumb kept focus.
        // The readouts under the track update live and carry the values.
        valueLabelDisplay="off"
        marks={[{ value: 0 }]}
        sx={{
          mt: '10px',
          mb: '2px',
          height: 8,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          // Outside the band: the zone that alarms.
          '& .MuiSlider-rail': {
            opacity: 1,
            background: `linear-gradient(to right, ${OUTSIDE}, rgba(220,53,69,0.06) 30%, rgba(220,53,69,0.06) 70%, ${OUTSIDE})`,
          },
          // The band itself.
          '& .MuiSlider-track': {
            border: 'none',
            backgroundColor: BAND_FILL,
          },
          '& .MuiSlider-thumb': {
            width: 16,
            height: 16,
            backgroundColor: '#ffffff',
            border: `2px solid ${OK_GREEN}`,
            boxShadow: '0 1px 4px rgba(16, 24, 40, 0.3)',
            '&:hover, &.Mui-focusVisible': {
              boxShadow: `0 0 0 6px rgba(47, 125, 84, 0.16)`,
            },
          },
          // The only mark is the centre line — the expected flow itself.
          // Strings, not numbers: sx treats a numeric width of 1 as 100%.
          '& .MuiSlider-mark': {
            width: '2px',
            height: '14px',
            backgroundColor: 'grey.800',
          },
        }}
      />
      {/* One line: low limit · EXPECTED FLOW (under its tick) · high limit.
          Both limits are typeable — the slider is for a rough sweep, the
          fields for the exact number the controller should use. */}
      <Stack direction="row" sx={{ alignItems: 'center', mt: '6px', gap: '8px' }}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}
        >
          <PctField
            sign="−"
            value={clampPct(low)}
            disabled={disabled}
            ariaLabel="Percent below expected flow that alarms"
            onChange={(next) => onChange(next, clampPct(high))}
          />
          <Typography sx={{ fontSize: '12px', color: 'grey.800' }}>
            {low >= 100 ? 'never alarms' : 'alarms below'}
          </Typography>
        </Stack>
        <Typography
          sx={{
            fontSize: '11.5px',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'text.main',
            whiteSpace: 'nowrap',
          }}
        >
          Expected Flow
        </Typography>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: '6px',
            flex: 1,
            minWidth: 0,
            justifyContent: 'flex-end',
          }}
        >
          <Typography sx={{ fontSize: '12px', color: 'grey.800' }}>
            {high >= 100 ? 'never alarms' : 'alarms above'}
          </Typography>
          <PctField
            sign="+"
            value={clampPct(high)}
            disabled={disabled}
            ariaLabel="Percent above expected flow that alarms"
            onChange={(next) => onChange(clampPct(low), next)}
          />
        </Stack>
      </Stack>
    </Box>
  )
}

interface IdleFlowSliderProps {
  /** Allowed idle flow, in display units. */
  value: number
  /** Scale end — the pipe's capacity in display units. */
  max: number
  unit: string
  disabled?: boolean
  onChange: (value: number) => void
}

/**
 * Idle flow on the same instrument, but its own axis: absolute flow, not a
 * percentage. Green up to the thumb is background flow the controller ignores
 * while no valve runs; anything beyond it is the Unexpected Flow zone.
 */
export const IdleFlowSlider = ({
  value,
  max,
  unit,
  disabled,
  onChange,
}: IdleFlowSliderProps) => {
  const scaleMax = max > 0 ? max : 100
  const step = scaleMax <= 20 ? 0.5 : 1

  return (
    <Box sx={{ px: '10px' }}>
      <Slider
        value={Math.min(value, scaleMax)}
        onChange={(_event, next) => {
          if (typeof next === 'number') onChange(next)
        }}
        disabled={disabled}
        min={0}
        max={scaleMax}
        step={step}
        valueLabelDisplay="auto"
        valueLabelFormat={(v) => `${v} ${unit}`}
        marks={[
          { value: 0, label: '0' },
          { value: scaleMax, label: `${scaleMax} ${unit}` },
        ]}
        sx={{
          mt: '10px',
          mb: '2px',
          height: 8,
          '& .MuiSlider-rail': {
            opacity: 1,
            background: `linear-gradient(to right, rgba(220,53,69,0.06), ${OUTSIDE})`,
          },
          '& .MuiSlider-track': {
            border: 'none',
            backgroundColor: BAND_FILL,
          },
          '& .MuiSlider-thumb': {
            width: 16,
            height: 16,
            backgroundColor: '#ffffff',
            border: `2px solid ${OK_GREEN}`,
            boxShadow: '0 1px 4px rgba(16, 24, 40, 0.3)',
            '&:hover, &.Mui-focusVisible': {
              boxShadow: `0 0 0 6px rgba(47, 125, 84, 0.16)`,
            },
          },
          '& .MuiSlider-mark': {
            // Strings, not numbers: sx treats a numeric width of 1 as 100%,
            // which turned the end marks into full-width bars jutting past
            // the rail.
            width: '1px',
            height: '10px',
            backgroundColor: 'rgba(138, 144, 153, 0.6)',
          },
        }}
      />
      <Typography sx={{ fontSize: '11px', color: 'grey.600', mt: '16px' }}>
        <Box component="b" sx={{ color: 'text.main', fontFamily: 'monospace' }}>
          {value} {unit}
        </Box>{' '}
        of background flow is tolerated while no valve is running — more raises
        Unexpected Flow
      </Typography>
    </Box>
  )
}

export default DeviationBandSlider
